import React from "react";
import { itemRevisionState } from "@/lib/orderRevision";

/**
 * Per-line-item revision ribbon. Renders nothing for items with no revision
 * stamps. Three variants:
 *   • Added (green):   "NEW · REV N"                — item was added in a recent revision
 *   • Removed (red):   "REMOVED UPSTREAM · REV N"   — item was dropped by Base44 but preserved for audit
 *   • Qty (yellow):    "QTY X → Y · REV N"          — quantity changed
 *
 * The `unseen` variant carries a starker outline / brighter tone so a warehouse
 * operator can eyeball a modified order and jump straight to what changed.
 */
export default function ItemRevisionRibbon({ item, order, size = "sm" }) {
  const state = itemRevisionState(item, order);
  if (!state) return null;

  const padding = size === "md" ? "px-2.5 py-1 text-[11px]" : "px-2 py-0.5 text-[10px]";

  let label;
  let colorCls;
  let testid;
  if (state.kind === "added") {
    label = `NEW · REV ${state.rev}`;
    colorCls = state.unseen
      ? "border-emerald-500 bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200 dark:border-emerald-600"
      : "border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800";
    testid = `item-ribbon-added-${item.sku}`;
  } else if (state.kind === "removed") {
    label = `REMOVED UPSTREAM · REV ${state.rev}`;
    colorCls = state.unseen
      ? "border-red-500 bg-red-100 text-red-900 dark:bg-red-950/60 dark:text-red-200 dark:border-red-600"
      : "border-red-300 bg-red-50 text-red-800 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800";
    testid = `item-ribbon-removed-${item.sku}`;
  } else if (state.kind === "qty") {
    const arrow = state.prev != null && state.next != null ? `${state.prev} → ${state.next}` : "changed";
    label = `QTY ${arrow} · REV ${state.rev}`;
    colorCls = state.unseen
      ? "border-amber-500 bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 dark:border-amber-600"
      : "border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800";
    testid = `item-ribbon-qty-${item.sku}`;
  } else {
    return null;
  }

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md border font-bold uppercase tracking-[0.12em] whitespace-nowrap ${padding} ${colorCls}`}
      data-testid={testid}
      title={state.unseen ? "New in the latest revision — not yet reviewed" : `Revision ${state.rev} · reviewed`}
    >
      {label}
    </span>
  );
}
